<template>
  <div class="user-profile">
    <a-form layout="vertical">
      <a-form-item :label="$t('user.nameFirst.label')" required>
        <a-input v-model:value="draft.nameFirst" :disabled="isSaving" />
      </a-form-item>

      <a-form-item :label="$t('user.nameLast.label')" required>
        <a-input v-model:value="draft.nameLast" :disabled="isSaving" />
      </a-form-item>

      <div class="user-profile__actions">
        <a-button type="primary" :loading="isSaving" :disabled="!canSave" @click="save">
          {{ $t("global.save") }}
        </a-button>
      </div>
    </a-form>

    <UserProfileFirebase />
  </div>
</template>

<script setup>
import { message } from "ant-design-vue"
import { computed, inject, reactive, ref, watch } from "vue"

import { tenantUser } from "@/init/authState"
import { i18n } from "@/i18n"
import reportError from "@/utils/reportError"

import UserProfileFirebase from "@/components/Telaryans/UserProfileFirebase.vue"

const props = defineProps({
  user: { type: Object, default: null }
})

const emit = defineEmits(["saved"])

const injectedUser = inject("currentUser", null)
const jobsServiceClient = inject("JobsServiceClient", null)

const fallbackUser = reactive({
  nameFirst: "",
  nameLast: ""
})

const userModel = computed(() => props.user || injectedUser || fallbackUser)

const draft = reactive({
  nameFirst: "",
  nameLast: ""
})

const isSaving = ref(false)

const replaceReactive = (target, source) => {
  if (!target || typeof target !== "object") return
  Object.keys(target).forEach((key) => delete target[key])
  if (source && typeof source === "object") {
    Object.entries(source).forEach(([key, value]) => {
      target[key] = value
    })
  }
}

const normalizeName = (value) => {
  const name = String(value ?? "").trim()
  return name || ""
}

const isBlank = (value) => String(value ?? "").trim().length === 0

watch(
  () => [userModel.value?.nameFirst, userModel.value?.nameLast],
  ([nameFirst, nameLast]) => {
    draft.nameFirst = nameFirst ?? ""
    draft.nameLast = nameLast ?? ""
  },
  { immediate: true }
)

const hasChanges = computed(() => {
  const currentFirst = normalizeName(userModel.value?.nameFirst)
  const currentLast = normalizeName(userModel.value?.nameLast)
  return normalizeName(draft.nameFirst) !== currentFirst || normalizeName(draft.nameLast) !== currentLast
})

const canSave = computed(() => {
  if (!jobsServiceClient) return false
  if (typeof jobsServiceClient?.updateMyFirebaseUser !== "function") return false
  if (isSaving.value) return false
  if (isBlank(draft.nameFirst) || isBlank(draft.nameLast)) return false
  return hasChanges.value
})

const save = async () => {
  if (!canSave.value) return
  if (typeof jobsServiceClient?.updateMyFirebaseUser !== "function") return

  const nameFirst = normalizeName(draft.nameFirst)
  const nameLast = normalizeName(draft.nameLast)

  isSaving.value = true
  try {
    const updatedUser = await jobsServiceClient.updateMyFirebaseUser({
      id: null,
      firebaseId: null,
      nameFirst,
      nameLast,
      email: null,
      mobileNumber: null,
      nickname: null
    })

    if (updatedUser) {
      if (props.user) replaceReactive(props.user, updatedUser)
      if (injectedUser) replaceReactive(injectedUser, updatedUser)
      replaceReactive(tenantUser, updatedUser)
      emit("saved", updatedUser)
    }
  } catch (error) {
    reportError(error, { context: "UserProfile.save" })
    message.error(i18n.global.t("global.errors.generic.body"))
  } finally {
    isSaving.value = false
  }
}
</script>

<style scoped lang="scss">
.user-profile {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.user-profile__actions {
  display: flex;
  justify-content: flex-end;
}
</style>
